import { matchPath } from 'react-router-dom';
import InvalidElement from '../../domain/Folder/InvalidElementInterface';
import Receipt from '../../domain/Receipt/Receipt';

const FOLDER_KEYS = ['form', 'direct', 'receipts', 'client_id', 'authorized_clients', 'invalidElements', 'to_re_sign', 'able_to_correct', 'dossier_state', 'validate', 'form_id', 'advisor_id'];
const TRACKED_FOLDERS_KEY = 'tracked_folder_ids';

export default class LocalStorageGateway {
  private explicitFolderId: string | null;

  constructor(folderId?: string | null) {
    // Store explicit folderId if provided, otherwise will be resolved dynamically
    this.explicitFolderId = folderId ?? null;
    const currentFolderId = this.getFolderId();
    if (currentFolderId) {
      this.trackFolderId(currentFolderId);
    }
  }

  private static getFolderIdFromUrl(): string | null {
    if (typeof window === 'undefined') return null;
    const match = matchPath<{ dossierId?: string }>(window.location.pathname, {
      path: '/dossier/:dossierId',
    });
    return match?.params?.dossierId ?? null;
  }

  private getFolderId(): string | null {
    // Use explicit folderId if provided, otherwise resolve from URL dynamically
    return this.explicitFolderId ?? LocalStorageGateway.getFolderIdFromUrl();
  }

  private getKey(baseKey: string): string {
    const folderId = this.getFolderId();
    return folderId ? `${folderId}_${baseKey}` : baseKey;
  }

  private trackFolderId(folderId: string): void {
    const tracked = localStorage.getItem(TRACKED_FOLDERS_KEY);
    const folderIds: string[] = tracked ? JSON.parse(tracked) : [];
    if (!folderIds.includes(folderId)) {
      folderIds.push(folderId);
      localStorage.setItem(TRACKED_FOLDERS_KEY, JSON.stringify(folderIds));
    }
  }

  static clearFolderData(folderId: string): void {
    FOLDER_KEYS.forEach(key => localStorage.removeItem(`${folderId}_${key}`));

    // Remove from tracked folders
    const tracked = localStorage.getItem(TRACKED_FOLDERS_KEY);
    if (tracked) {
      const folderIds: string[] = JSON.parse(tracked);
      const updatedIds = folderIds.filter(id => id !== folderId);
      if (updatedIds.length > 0) {
        localStorage.setItem(TRACKED_FOLDERS_KEY, JSON.stringify(updatedIds));
      } else {
        localStorage.removeItem(TRACKED_FOLDERS_KEY);
      }
    }
  }

  static clearAllFolderData(): void {
    // Clear legacy non-namespaced keys
    FOLDER_KEYS.forEach(key => localStorage.removeItem(key));

    // Clear all namespaced folder keys
    const tracked = localStorage.getItem(TRACKED_FOLDERS_KEY);
    if (tracked) {
      const folderIds: string[] = JSON.parse(tracked);
      folderIds.forEach(folderId => {
        FOLDER_KEYS.forEach(key => localStorage.removeItem(`${folderId}_${key}`));
      });
      localStorage.removeItem(TRACKED_FOLDERS_KEY);
    }
  }

  save(datas: Array<string>): Promise<boolean> {
    return new Promise((resolve) => {

      const values = {}
      datas.map(data => {
        values[data['element_id']] = data['value']
      })

      localStorage.setItem(this.getKey('form'), JSON.stringify(values))

      resolve(true)
    })
  }

  saveDirect(direct: boolean): Promise<boolean> {
    return new Promise((resolve) => {

      localStorage.setItem(this.getKey('direct'), String(direct))

      resolve(true)
    })
  }

  saveReceipts(receipts: Receipt[]|null): Promise<boolean> {
    return new Promise((resolve) => {

      localStorage.setItem(this.getKey('receipts'), JSON.stringify(receipts))

      resolve(true)
    })
  }

  saveClientId(clientId: number|null): Promise<boolean> {
    return new Promise((resolve) => {

      localStorage.setItem(this.getKey('client_id'), String(clientId))

      resolve(true)
    })
  }

  saveAuthorizedClients(clientsId: number[]): Promise<boolean> {
    return new Promise((resolve) => {

      localStorage.setItem(this.getKey('authorized_clients'), JSON.stringify(clientsId))

      resolve(true)
    })
  }

  saveInvalidElements(invalidElements: InvalidElement[]|null): Promise<boolean> {
    return new Promise((resolve) => {

      localStorage.setItem(this.getKey('invalidElements'), JSON.stringify(invalidElements))

      resolve(true)
    })
  }

  saveToReSign(toReSign: boolean|null): Promise<boolean> {
    return new Promise((resolve) => {

      localStorage.setItem(this.getKey('to_re_sign'), String(toReSign))

      resolve(true)
    })
  }

  saveAdvisorId(advisorId: string|null): Promise<boolean> {
    return new Promise((resolve) => {

      localStorage.setItem(this.getKey('advisor_id'), String(advisorId))

      resolve(true)
    })
  }

  saveUnablePlateformToCorrect(toReSign: boolean|null): Promise<boolean> {
    return new Promise((resolve) => {

      localStorage.setItem(this.getKey('able_to_correct'), String(toReSign))

      resolve(true)
    })
  }
}
